<script setup>
import { ref, reactive, computed, nextTick, watch, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const emit = defineEmits(['show-notification', 'editar-item', 'adicionar-item', 'duplicar-item'])
const emitNotification = (p) => emit('show-notification', p)

// ============================================================
// Estado
// ============================================================
const lista = ref([])
const isLoading = ref(false)
const filtrosAbertos = ref(false)
const listas = reactive({})

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({ busca: '', lstStatus: '' })

// Estado da exportação
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const podeEditar = computed(() => authStore.pode('PERFIL_TRIBUTARIO_COMPLETO'))

// ============================================================
// Descrição textual dos filtros ativos (para exportação)
// ============================================================
const descricaoFiltros = computed(() => {
  const partes = []
  if (filtros.busca) partes.push(`Busca: "${filtros.busca}"`)
  if (filtros.lstStatus) {
    const statusItem = (listas.LstStatus || []).find(s =>
      String(s.idListaSistemaItem) === String(filtros.lstStatus)
    )
    partes.push(`Status: ${statusItem?.textoLista || filtros.lstStatus}`)
  }
  return partes.length ? partes.join(' | ') : 'Nenhum filtro aplicado'
})

// ============================================================
// Colunas — definição completa de TODOS os campos do DTO
// ============================================================
const colunasPadrao = [
  // Identificação
  { field: 'idPerfilTributario', label: 'ID', width: 80, sortable: true },
  { field: 'descricao', label: 'Descrição do Perfil', sortable: true, class: 'col-3 fw-large text-body-emphasis' },
  { field: 'lstStatus', label: 'Status', align: 'center', sortable: true },

  // CST / CSOSN
  { field: 'lstCstIcms', label: 'CST ICMS', width: 130, sortable: true, align: 'center' },
  { field: 'lstCsosn', label: 'CSOSN', width: 130, sortable: true, align: 'center' },
  { field: 'lstCstPis', label: 'CST PIS', width: 120, sortable: true, align: 'center', visible: false },
  { field: 'lstCstCofins', label: 'CST COFINS', width: 120, sortable: true, align: 'center', visible: false },
  { field: 'lstCstIpi', label: 'CST IPI', width: 120, sortable: true, align: 'center', visible: false },
  { field: 'lstNaturezaItemAtendido', label: 'Natureza Item Atendido', width: 160, sortable: true, align: 'center', visible: false },

  // Alíquotas Federais / Comex
  { field: 'ipiAliquota', label: 'Alíq. IPI (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'pisAliquota', label: 'Alíq. PIS (%)', align: 'right', sortable: true, formatter: formatarPercentual },
  { field: 'cofinsAliquota', label: 'Alíq. COFINS (%)', align: 'right', sortable: true, formatter: formatarPercentual },
  { field: 'iiAliquota', label: 'Alíq. II (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'ieAliquota', label: 'Alíq. IE (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },

  // Alíquotas Estaduais
  { field: 'icmsAliquota', label: 'Alíq. ICMS (%)', align: 'right', sortable: true, formatter: formatarPercentual },
  { field: 'difalAliquota', label: 'Alíq. DIFAL (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'icmsAliquotaEfetiva', label: 'Alíq. ICMS Efetiva (%)', align: 'right', sortable: true, formatter: formatarPercentual },

  // Substituição Tributária (ICMS-ST)
  { field: 'icmsStMvaPercentual', label: 'ICMS-ST MVA (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'icmsStAliquota', label: 'ICMS-ST Alíq. (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'icmsStReducaoBasePercentual', label: 'ICMS-ST Red. Base (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },

  // Incentivos Estaduais
  { field: 'lstTipoIncentivoIcms', label: 'Tipo de Incentivo ICMS', width: 160, sortable: true, visible: false },
  { field: 'percentualCreditoPresumidoIcms', label: 'Crédito Presumido ICMS (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },

  // Reforma Tributária
  { field: 'cbsAliquota', label: 'Alíq. CBS (%)', align: 'right', sortable: true, formatter: formatarPercentual },
  { field: 'ibsAliquota', label: 'Alíq. IBS (%)', align: 'right', sortable: true, formatter: formatarPercentual },
  { field: 'isAliquota', label: 'Alíq. IS (%)', align: 'right', sortable: true, formatter: formatarPercentual },

  // Vigência Temporal
  { field: 'dataInicioVigencia', label: 'Início Vigência', align: 'center', sortable: true, visible: false, formatter: formatarData },
  { field: 'dataFimVigencia', label: 'Fim Vigência', align: 'center', sortable: true, visible: false, formatter: formatarData },

  // Impostos Municipais (ISS)
  { field: 'issAliquota', label: 'Alíq. ISS (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'reterIssFonte', label: 'Reter ISS Fonte', align: 'center', sortable: true, visible: false, formatter: formatarBooleano },

  // Retenções Federais na Fonte
  { field: 'irrfRetidoAliquota', label: 'IRRF Retido (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'inssRetidoAliquota', label: 'INSS Retido (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'csllRetidaAliquota', label: 'CSLL Retida (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'pisRetidoAliquota', label: 'PIS Retido (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'cofinsRetidaAliquota', label: 'COFINS Retida (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },

  // Propriedades Calculadas
  { field: 'ganhoIncentivo', label: 'Ganho Incentivo (%)', align: 'right', sortable: true, visible: false, formatter: formatarPercentual },
  { field: 'fatorImpostos', label: 'Fator Impostos', align: 'right', sortable: true, visible: false, formatter: (val) => val != null ? Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '-' },
  { field: 'fatorImpostosMemoriaJson', label: 'Memória Fator Impostos', width: 200, sortable: false, visible: false },

  // Auditoria
  { field: 'tsCriacao', label: 'Criado em', align: 'center', sortable: true, visible: false, formatter: formatarDataHora },
  { field: 'tsModificacao', label: 'Modificado em', align: 'center', sortable: true, visible: false, formatter: formatarDataHora },
  { field: 'nomeUsuarioCriacao', label: 'Criado por', width: 160, sortable: true, visible: false },
  { field: 'nomeUsuarioModificacao', label: 'Modificado por', width: 160, sortable: true, visible: false },

  // Organização (chave lógica)
  { field: 'idOrganizacao', label: 'Organização', width: 100, sortable: true, visible: false }
]

// Mapeamento campo → nome do slot no template (apenas campos com customização visual)
const campoSlotMap = {
  idPerfilTributario: 'id',
  lstCstIcms: 'lstCstIcms',
  lstCsosn: 'lstCsosn',
  lstStatus: 'lstStatus',
  acoes: 'acoes'
}

// Colunas efetivas (com slotName) + coluna de ações no final
const colunas = computed(() => {
  const base = colunasPadrao.map(c => ({
    ...c,
    slotName: campoSlotMap[c.field] || undefined
  }))
  return [
    ...base,
    { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
  ]
})

// ============================================================
// Formatação
// ============================================================
function formatarPercentual (valor) {
  if (valor == null) return '-'
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function formatarData (valor) {
  if (!valor) return '-'
  const d = typeof valor === 'string' ? new Date(valor + 'T12:00:00') : new Date(valor)
  return isNaN(d.getTime()) ? '-' : formatarDataBR(d)
}

function formatarDataHora (valor) {
  if (!valor) return '-'
  const d = typeof valor === 'string' ? new Date(valor) : new Date(valor)
  return isNaN(d.getTime()) ? '-' : formatarDataHoraBR(d)
}

function formatarBooleano (valor) {
  return valor ? 'Sim' : 'Não'
}

// ============================================================
// Sort Resolver (client-side para campos Lst*)
// Usa os campos {campo}Texto injetados pelo AdminTable via resolverListasTabela
// ============================================================
const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  return [...data].sort((a, b) => {
    const valA = a[field + 'Texto'] || a[field] || ''
    const valB = b[field + 'Texto'] || b[field] || ''
    const cmp = String(valA).localeCompare(String(valB), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

// ============================================================
// Carregamento de listas (para filtros)
// ============================================================
async function carregarListas () {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['PerfilTributario'])
    Object.assign(listas, result)
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

// ============================================================
// Busca de dados
// ============================================================
async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) { lista.value = []; return }
  isLoading.value = true
  const params = injetarParametros(pageNumber)
  if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
  if (filtros.busca) params.append('busca', filtros.busca)
  if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
  if (sortField.value) params.append('sortField', sortField.value)
  if (sortOrder.value) params.append('sortOrder', sortOrder.value)

  try {
    const response = await api.get(`PerfisTributarios?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) { console.error('Erro:', error) } finally { isLoading.value = false }
}

// ============================================================
// Exportação
// ============================================================
async function handleExportarDados () {
  if (!authStore.empresaAtiva) {
    emitNotification({ message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  const totalRegistros = paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emitNotification({ message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)

      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (filtros.busca) params.append('busca', filtros.busca)
      if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)

      const response = await api.get(`PerfisTributarios?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]
    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[PerfisTributarios] Erro na exportação:', e)
    emitNotification({ message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

// ============================================================
// Ações
// ============================================================
function aplicarFiltros () { buscarDados(1) }
function limparFiltros () { filtros.busca = ''; filtros.lstStatus = ''; buscarDados(1) }
function novoItem () { emit('adicionar-item') }
function editarItem (item) { emit('editar-item', item) }
function duplicarItem (item) { emit('duplicar-item', item) }
function abrirAuditoria (item) {
  auditoriaSelecionada.id = item.idPerfilTributario
  auditoriaSelecionada.nome = item.descricao
  mostrarAuditoria.value = true
}

// ============================================================
// Ciclo de vida
// ============================================================
watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Perfis Tributários"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="podeEditar"
    @mudar-pagina="buscarDados"
    @mudar-tamanho-pagina="buscarDados(1)"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3">
        <div class="col-md-4">
          <input class="form-control" placeholder="Buscar por descrição..." v-model="filtros.busca" @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatus || [])" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros" :disabled="isLoading">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            Aplicar Filtros
          </button>
        </div>
      </div>
    </template>

    <div v-if="isLoading && lista.length === 0" class="text-center p-5"><div class="spinner-border text-primary"></div></div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="isLoading"
      empty-message="Nenhum perfil tributário encontrado para os filtros aplicados."
      row-key="idPerfilTributario"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      modulo-layout="PerfisTributarios"
      :colunas-padrao="colunasPadrao"
      resolver-listas-tabela="PerfilTributario"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Perfis Tributários"
      :filtros-exportacao="descricaoFiltros"
      @sort-change="handleSortChange"
      @show-notification="emitNotification"
      @exportar-dados="handleExportarDados"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idPerfilTributario }}</span>
      </template>

      <template #lstCstIcms="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ row.lstCstIcmsTexto || row.lstCstIcms || '-' }}
        </span>
      </template>

      <template #lstCsosn="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ row.lstCsosnTexto || row.lstCsosn || '-' }}
        </span>
      </template>

      <template #lstStatus="{ row }">
        <span :class="obterClasseStatusGhost(row.lstStatusTexto || row.lstStatus || '-')">
          {{ row.lstStatusTexto || row.lstStatus || '-' }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico de Alterações">
            <i class="bi bi-clock-history"></i>
          </button>
          <button v-if="podeEditar" class="btn-icon text-info" @click="duplicarItem(row)" title="Duplicar Perfil">
            <i class="bi bi-copy"></i>
          </button>
          <button class="btn-icon text-primary" @click="editarItem(row)" :title="podeEditar ? 'Editar Perfil' : 'Visualizar Perfil'">
            <i :class="podeEditar ? 'bi bi-pencil-fill' : 'bi bi-eye-fill'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="PerfilTributario"
    contexto="tenant"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
